import Service, { service } from '@ember/service';
import { dropTask, task } from 'ember-concurrency';
import * as calendarCache from 'my-phorest/utils/calendar-cache';
import {
  evictAppointments,
  evictBookingHistory,
  evictClientCourses,
  evictServiceRewardsForClient,
} from 'my-phorest/utils/graphql';
import { evictAppointmentsFromResourcesCalendar } from 'my-phorest/utils/calendar-cache';
import { sendCalendarSynchronizationIssueToSentry } from 'my-phorest/utils/calendar';
import sortAppointments from 'my-phorest/utils/sort-appointments';

import appointmentCalendarFragment from 'my-phorest/gql/fragments/appointment-calendar.graphql';
import staffMemberFragment from 'my-phorest/gql/fragments/staff-member.graphql';

import appointmentsQuery from 'my-phorest/gql/queries/appointments.graphql';
import createAppointment from 'my-phorest/gql/mutations/create-appointment.graphql';
import createAppointments from 'my-phorest/gql/mutations/create-appointments.graphql';
import createServiceGroupAppointment from 'my-phorest/gql/mutations/create-service-group-appointment.graphql';
import createClientCourseAppointment from 'my-phorest/gql/mutations/create-client-course-appointment.graphql';

export default class AppointmentTasksService extends Service {
  @service apollo;
  @service intl;
  @service notifications;
  @service session;

  @dropTask
  *createAppointmentTask(variables = {}) {
    const response = yield this.apollo.mutate({
      mutation: createAppointment,
      variables,
      update: (cache, { data: { createAppointment } }) => {
        const { appointment } = createAppointment;
        if (appointment) {
          calendarCache.addEventsToCalendarCache(cache, appointment);
        }
      },
    });
    return response.createAppointment;
  }

  @dropTask
  *createPackageAppointmentTask(variables = {}) {
    const response = yield this.apollo.mutate({
      mutation: createServiceGroupAppointment,
      variables,
      update: (cache, { data: { createServiceGroupAppointment } }) => {
        const { appointments } = createServiceGroupAppointment;
        if (appointments) {
          calendarCache.addEventsToCalendarCache(cache, appointments);
        }
      },
    });
    return response.createServiceGroupAppointment;
  }

  @task
  *fetchClientVisitTask(
    clientId,
    date,
    { useCalendarCacheIfPossible = false, queryFetchPolicy } = {}
  ) {
    if (!clientId)
      throw new Error('`clientId` is required to fetch client visit');
    if (!date) throw new Error('`date` is required to fetch client visit');

    if (useCalendarCacheIfPossible) {
      let appointmentsInCache = this.#readClientVisitDataFromCache(
        clientId,
        date
      );
      if (appointmentsInCache.length > 0) {
        return [...appointmentsInCache].sort(sortAppointments);
      }
    }

    const { appointments } = yield this.apollo.query({
      query: appointmentsQuery,
      variables: {
        first: 100,
        filterBy: {
          clientId,
          startDate: date,
          endDate: date,
        },
      },
      fetchPolicy: queryFetchPolicy,
    });

    if (appointments) {
      return [...appointments.edges.map((edge) => edge.node)].sort(
        sortAppointments
      );
    }
    return [];
  }

  /**
   * Used in "potential Calendar de-synchronization issue" on appointment update attempt
   * when the appointment was deleted before.
   *
   * Removes the calendar cache, informs Sentry about the issue and shows an info notification
   */
  handleDeletedAppointmentUpdateAttempt() {
    sendCalendarSynchronizationIssueToSentry(
      'updating previously removed appointment'
    );
    calendarCache.invalidate(this.apollo.client.cache);
    this.notifications.info(
      this.intl.t(
        'appointments.synchronization.appointment-already-deleted.title'
      ),
      this.intl.t(
        'appointments.synchronization.appointment-already-deleted.description'
      ),
      { sticky: true }
    );
  }

  /**
   * Used in "potential Calendar de-synchronization issue" on appointment deletion attempt
   * when the appointment was deleted or cancelled before.
   *
   * Removes appointment from calendar (and related objects), removes whole calendar cache, and informs Sentry about the issue.
   *
   * @param {Array} appointments
   */
  removePreviouslyDeletedAppointments(appointments) {
    sendCalendarSynchronizationIssueToSentry(
      'removing previously removed/cancelled appointment'
    );
    this.updateCacheAfterAppointmentRemoval(appointments);
    const apolloCache = this.apollo.client.cache;
    calendarCache.invalidate(apolloCache);
  }

  /**
   * Removes appointments from calendar cache and invalidates related queries and objects.
   * It should be used for both: deleted and cancelled appointments as they both result in appointment's removal from the calendar.
   * In case we are removing a recurrent appointment(s) (@arg isRecurrent = true), the whole calendar cache will be invalidated.
   *
   * @param {Array} appointments
   * @param {Boolean} [isRecurrent=false]
   */
  updateCacheAfterAppointmentRemoval(appointments, isRecurrent = false) {
    const cache = this.apollo.client.cache;
    const appointmentIds = appointments.map((appt) => appt.id);
    const clientIds = [...new Set(appointments.map((appt) => appt.client.id))];

    evictAppointments(cache, appointmentIds);
    evictAppointmentsFromResourcesCalendar(cache, appointments);
    evictBookingHistory(cache);

    for (const clientId of clientIds) {
      evictClientCourses(cache, clientId);
      cache.evict({
        id: cache.identify({ __typename: 'Client', id: clientId }),
        fieldName: 'treatCardAccount',
      });
      evictServiceRewardsForClient(cache, clientId, this.session.branchList);
    }

    if (isRecurrent) {
      calendarCache.invalidate(cache);
    }

    cache.gc();
  }

  #readClientVisitDataFromCache(clientId, date) {
    const cacheData = this.apollo.client.cache.extract();
    const appointmentIds = [];

    for (const key in cacheData) {
      const value = cacheData[key];
      if (
        value.__typename === 'Appointment' &&
        value.client?.__ref === `Client:${clientId}` &&
        value.date === date
      ) {
        appointmentIds.push(value.id);
      }
    }

    return appointmentIds
      .map((id) => {
        const appointment = this.apollo.client.cache.readFragment({
          id: `Appointment:${id}`,
          fragment: appointmentCalendarFragment,
          fragmentName: 'appointmentCalendarFields',
        });
        const staffMember = this.apollo.client.cache.readFragment({
          id: `Staff:${appointment.staffMemberId}`,
          fragment: staffMemberFragment,
        });
        if (!staffMember) return null;

        return {
          ...appointment,
          staffMember,
        };
      })
      .filter(Boolean);
  }

  @task
  *createClientCourseAppointmentTask(variables = {}) {
    const response = yield this.apollo.mutate(
      {
        mutation: createClientCourseAppointment,
        variables,
        update: (cache, { data: { createClientCourseAppointment } }) => {
          const { appointment } = createClientCourseAppointment;
          if (appointment) {
            calendarCache.addEvent(cache, {
              date: appointment.date,
              eventId: appointment.id,
              resourceId: appointment.staffMemberId,
            });
            if (appointment.machine?.id) {
              calendarCache.addEvent(cache, {
                date: appointment.date,
                eventId: appointment.id,
                resourceId: appointment.machine.id,
                resourceType: 'Machine',
              });
            }
            if (appointment.room?.id) {
              calendarCache.addEvent(cache, {
                date: appointment.date,
                eventId: appointment.id,
                resourceId: appointment.room.id,
                resourceType: 'Room',
              });
            }
            evictBookingHistory(cache);
            cache.gc();
          }
        },
      },
      'createClientCourseAppointment'
    );

    return response;
  }

  @task
  *createPackageAppointmentsTask(data) {
    let variables = {
      clientId: data.clientId,
      acceptedIssues: data.issues,
      date: data.date,
      serviceSchedules: data.packageOptions,
    };
    return yield this.apollo.mutate(
      {
        mutation: createAppointments,
        variables,
        update: (cache, { data: { createAppointments } }) => {
          const { appointments } = createAppointments;
          if (appointments) {
            calendarCache.addEventsToCalendarCache(cache, appointments);
          }
        },
      },
      'createAppointments'
    );
  }
}
